import { useEffect, useRef, useState } from "react";
import type { Editor } from "@tiptap/core";
import { TableInsertDialog, type InsertLands } from "./TableInsertDialog";
import { showLandedBlock } from "./landedBlock";
import { placeViaServer, type PlaceBlock } from "./tableLinking";
import { useTables } from "./useTables";
import {
  OPEN_TABLE_PICKER_EVENT, dispatchToolboxInsert, type OpenTablePickerDetail,
} from "@/components/toolbox/insertHandler";
import { describeLandings, landingPosition, type EditorLanding } from "./editorLanding";
import { blockToTiptapNode } from "@/lib/tiptap/document-serializer";
import { generateUUID } from "@/utils/uuid";
import type { Block } from "@/lib/api";

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

/**
 * Put a table into the open paper through the editor, not behind its back.
 *
 * The canvas owns an open document: a block the server adds is not shown until
 * a reload, and while the canvas has unsaved edits its next save can drop it.
 * So the block goes into the editor, with an id chosen here, where the `/` was
 * (or after the block holding the caret), and the editor's own save writes it.
 * The link then names that id. With no live editor, the server adds it.
 */
export function placeInEditor(
  getEditor: () => Editor | null,
  atSlash: boolean,
  fallback: PlaceBlock,
  /** Where it lands: at the caret (the default), the section's end, the document's. */
  landing: () => EditorLanding = () => "caret",
): PlaceBlock {
  return async (documentId, content) => {
    const editor = getEditor();
    if (!editor || editor.isDestroyed) return fallback(documentId, content);
    const id = generateUUID();
    const node = blockToTiptapNode({ id, documentId, type: "table", content, sortOrder: 0 } as unknown as Block);
    if (!node) return null;
    const at = landingPosition(editor, landing(), atSlash);
    const ok = editor.chain().focus().insertContentAt(at, node).run();
    return ok ? id : null;
  };
}

/**
 * "Existing table…" — the editor's Insert menu and slash menu open this over the
 * author's tables. Mounted once by the editor page, which knows the document.
 */
export function EditorTablePicker({
  documentId,
  fetchWithAuth,
  getEditor,
}: {
  documentId: string | undefined;
  fetchWithAuth: Fetch;
  getEditor: () => Editor | null;
}) {
  const [open, setOpen] = useState<OpenTablePickerDetail | null>(null);

  useEffect(() => {
    const onOpen = (e: Event) => setOpen((e as CustomEvent<OpenTablePickerDetail | undefined>).detail ?? {});
    window.addEventListener(OPEN_TABLE_PICKER_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_TABLE_PICKER_EVENT, onOpen);
  }, []);

  if (!open || !documentId) return null;
  return (
    <Picker
      documentId={documentId}
      fetchWithAuth={fetchWithAuth}
      getEditor={getEditor}
      atSlash={!!open.atSlash}
      onClose={() => setOpen(null)}
    />
  );
}

/**
 * Mounted only while open, so the list is fresh each time. The Lands row is
 * read from the canvas as the sheet opens; ⇥ moves it between the caret, the
 * end of the caret's section, and the end of the paper.
 */
function Picker({
  documentId, fetchWithAuth, getEditor, atSlash, onClose,
}: {
  documentId: string;
  fetchWithAuth: Fetch;
  getEditor: () => Editor | null;
  atSlash: boolean;
  onClose: () => void;
}) {
  const { tables } = useTables(fetchWithAuth);
  const [landing, setLanding] = useState<EditorLanding>("caret");
  const landingRef = useRef(landing);
  landingRef.current = landing;

  // Read once, as the sheet opens: the caret does not move while it is up.
  const [info] = useState(() => {
    const e = getEditor();
    return e && !e.isDestroyed ? describeLandings(e) : null;
  });
  const [place] = useState(() =>
    placeInEditor(getEditor, atSlash, placeViaServer(fetchWithAuth), () => landingRef.current));

  const lands: InsertLands | undefined = info
    ? {
        options: [
          { id: "caret", title: "At your caret", detail: info.caret },
          ...(info.section ? [{ id: "section" as const, title: "End of section", detail: info.section.replace(/^End of /, "") }] : []),
          { id: "end", title: "At the end", detail: "after the paper's last block" },
        ],
        value: landing,
        onChange: setLanding,
      }
    : undefined;

  /** Esc returns the caret exactly where it was. */
  const close = () => {
    onClose();
    const e = getEditor();
    if (e && !e.isDestroyed) e.commands.focus();
  };

  return (
    <TableInsertDialog
      mode="pickTable"
      fetchWithAuth={fetchWithAuth}
      tables={tables ?? undefined}
      documentId={documentId}
      place={place}
      lands={lands}
      onClose={close}
      onDone={(r) => {
        onClose();
        if (r.blockId) showLandedBlock(r.blockId);
      }}
      onGoTo={(blockId) => showLandedBlock(blockId)}
      onNewBlankTable={() => {
        const e = getEditor();
        if (e && !e.isDestroyed) e.commands.focus();
        dispatchToolboxInsert({ kind: "table" }, "toolbox");
      }}
    />
  );
}
